import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authFetch } from '../../../utils/authFetch';

export const fetchFindings = createAsyncThunk(
  'agencyFindings/fetchAll',
  async ({ assessmentId, severity, status, finding_type, parent_finding_id, include_deleted }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ assessmentId });
      if (severity) params.set('severity', severity);
      if (status) params.set('status', status);
      if (finding_type) params.set('finding_type', finding_type);
      if (parent_finding_id) params.set('parent_finding_id', parent_finding_id);
      if (include_deleted) params.set('include_deleted', 'true');
      const response = await authFetch(`/.netlify/functions/agency-findings-list?${params}`, { method: 'GET' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createFinding = createAsyncThunk(
  'agencyFindings/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-findings-create', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const updateFinding = createAsyncThunk(
  'agencyFindings/update',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-findings-update', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteFinding = createAsyncThunk(
  'agencyFindings/delete',
  async ({ findingId }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-findings-delete', {
        method: 'POST',
        body: JSON.stringify({ findingId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      const data = await response.json();
      return { findingId, finding: data.finding };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const restoreFinding = createAsyncThunk(
  'agencyFindings/restore',
  async ({ findingId }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-findings-restore', {
        method: 'POST',
        body: JSON.stringify({ findingId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const reorderFindings = createAsyncThunk(
  'agencyFindings/reorder',
  async ({ assessmentId, findingType, orderedIds }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-findings-reorder', {
        method: 'POST',
        body: JSON.stringify({ assessmentId, findingType, orderedIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return { findingType, orderedIds };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const discardFinding = createAsyncThunk(
  'agencyFindings/discard',
  async ({ findingId, reason }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-findings-discard', {
        method: 'POST',
        body: JSON.stringify({ findingId, reason }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const refineFindingLLM = createAsyncThunk(
  'agencyFindings/refineLLM',
  async ({ findingId, field, instruction }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-findings-refine-llm', {
        method: 'POST',
        body: JSON.stringify({ findingId, field, instruction }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const suggestFindingName = createAsyncThunk(
  'agencyFindings/suggestName',
  async ({ findingId }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-findings-suggest-name', {
        method: 'POST',
        body: JSON.stringify({ findingId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const reviewFinding = createAsyncThunk(
  'agencyFindings/review',
  async ({ findingId, status, comment }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/agency-findings-review', {
        method: 'POST',
        body: JSON.stringify({ findingId, status, comment }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const agencyFindingsSlice = createSlice({
  name: 'agencyFindings',
  initialState: {
    items: [],
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearFindingsError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFindings.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchFindings.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.findings || [];
      })
      .addCase(fetchFindings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch findings';
      })
      .addCase(createFinding.fulfilled, (state, action) => {
        const created = action.payload.finding;
        if (created) state.items.push(created);
      })
      .addCase(updateFinding.pending, (state) => { state.saving = true; })
      .addCase(updateFinding.fulfilled, (state, action) => {
        state.saving = false;
        const updated = action.payload.finding;
        if (updated) {
          const idx = state.items.findIndex(f => f.id === updated.id);
          if (idx >= 0) state.items[idx] = updated;
        }
      })
      .addCase(updateFinding.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || 'Failed to update finding';
      })
      .addCase(deleteFinding.fulfilled, (state, action) => {
        // Soft delete — keep the row but mark deleted_at so include_deleted refetches still work
        const id = action.payload.findingId;
        const idx = state.items.findIndex(f => f.id === id);
        if (idx >= 0) state.items[idx] = action.payload.finding || { ...state.items[idx], deleted_at: new Date().toISOString() };
      })
      .addCase(restoreFinding.fulfilled, (state, action) => {
        const restored = action.payload.finding;
        if (restored) {
          const idx = state.items.findIndex(f => f.id === restored.id);
          if (idx >= 0) state.items[idx] = restored;
        }
      })
      .addCase(reorderFindings.fulfilled, (state, action) => {
        const { findingType, orderedIds } = action.payload;
        // Apply local sort_order so the UI updates without a refetch
        const orderMap = new Map(orderedIds.map((id, i) => [id, i]));
        state.items = state.items.map((f) => (
          f.finding_type === findingType && orderMap.has(f.id)
            ? { ...f, sort_order: orderMap.get(f.id) }
            : f
        ));
      })
      .addCase(discardFinding.fulfilled, (state, action) => {
        const updated = action.payload.finding;
        if (updated) {
          const idx = state.items.findIndex(f => f.id === updated.id);
          if (idx >= 0) state.items[idx] = updated;
        }
      })
      .addCase(reviewFinding.fulfilled, (state /* , action */) => {
        // Review row is created — the finding's status is mirrored server-side.
        // Caller refetches if it cares about updated finding row.
        state.saving = false;
      });
  },
});

export const { clearFindingsError } = agencyFindingsSlice.actions;
export const selectFindings = (state) => state.agencyFindings?.items || [];
export const selectFindingsLoading = (state) => state.agencyFindings?.loading || false;
export const selectFindingsSaving = (state) => state.agencyFindings?.saving || false;
export const selectFindingsError = (state) => state.agencyFindings?.error || null;
export default agencyFindingsSlice.reducer;
